<template>
  <div class="pagination" v-if="totalPage > 1">
    <!-- 分页信息 -->
    <div class="page-info">
      共 {{ totalCount }} 条数据，当前第 {{ currentPage }} / {{ totalPage }} 页
    </div>

    <!-- 页码按钮 -->
    <div class="page-buttons">
      <!-- 上一页 -->
      <button 
        class="page-btn prev" 
        @click="changePage(currentPage - 1)" 
        :disabled="currentPage === 1"
      >
        上一页
      </button>

      <!-- 页码列表 -->
      <template v-for="page in pageRange" :key="page">
        <button
          v-if="page !== '...'"
          class="page-btn number"
          :class="{ active: page === currentPage }"
          @click="changePage(page)"
        >
          {{ page }}
        </button>
        <span v-else class="ellipsis">...</span>
      </template>

      <!-- 下一页 -->
      <button 
        class="page-btn next" 
        @click="changePage(currentPage + 1)" 
        :disabled="currentPage === totalPage"
      >
        下一页
      </button>
    </div>

    <!-- ✅ 输入跳转功能 -->
    <div class="page-jump">
      前往
      <input 
        type="number" 
        v-model.number="jumpPage" 
        @keyup.enter="jumpToPage"
        min="1"
        :max="totalPage"
      />
      页
      <button class="page-btn go" @click="jumpToPage">跳转</button>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, defineEmits, defineProps } from 'vue'

const props = defineProps({
  totalCount: { type: Number, required: true },
  currentPage: { type: Number, required: true },
  pageSize: { type: Number, default: 12 }
})

const emit = defineEmits(['update:currentPage', 'page-change'])

// 总页数
const totalPage = computed(() => Math.ceil(props.totalCount / props.pageSize))

// 页码范围
const pageRange = computed(() => {
  const pages = []
  const total = totalPage.value
  const current = props.currentPage
  const maxPages = 5

  if (total <= maxPages) {
    for (let i = 1; i <= total; i++) pages.push(i)
  } else {
    pages.push(1)
    let start = Math.max(2, current - 1)
    let end = Math.min(total - 1, current + 1)

    if (current <= 2) {
      end = 3
    } else if (current >= total - 1) {
      start = total - 2
    }

    if (start > 2) pages.push('...')
    for (let i = start; i <= end; i++) pages.push(i)
    if (end < total - 1) pages.push('...')
    pages.push(total)
  }

  return pages
})

// 页码变化
const changePage = (page) => {
  if (page < 1 || page > totalPage.value || page === props.currentPage) return
  emit('update:currentPage', page)
  emit('page-change', page)
}

// ✅ 输入页码跳转
const jumpPage = ref('')

const jumpToPage = () => {
  const page = Number(jumpPage.value)
  if (!page || page < 1 || page > totalPage.value) {
    jumpPage.value = ''
    return
  }
  changePage(page)
  jumpPage.value = ''
}
</script>

<style scoped>
.pagination {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 20px;
  margin: 30px 0;
  padding: 10px;
}

.page-info {
  color: #666;
  font-size: 14px;
}

.page-buttons {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.page-btn {
  padding: 5px 12px;
  border: 1px solid #ddd;
  background-color: #fff;
  color: #333;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
}
.page-btn:disabled {
  cursor: not-allowed;
  color: #999;
  background-color: #f5f5f5;
  border-color: #eee;
}
.page-btn.number.active {
  background-color: #003366;
  color: #fff;
  border-color: #003366;
}
.page-btn:hover:not(:disabled) {
  border-color: #003366;
  color: #003366;
}
.ellipsis {
  color: #999;
  padding: 0 6px;
}

/* ✅ 输入跳转部分样式 */
.page-jump {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: #666;
}
.page-jump input {
  width: 50px;
  padding: 4px 6px;
  border: 1px solid #ddd;
  border-radius: 4px;
  text-align: center;
  font-size: 14px;
}
.page-btn.go {
  padding: 5px 10px;
}
</style>
